import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PublicBrowseShell from '../../components/public/PublicBrowseShell';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Input from '../../components/ui/Input';
import { regionAssociationService } from '../../services/regionAssociationService';

const RegionsListPage = () => {
  const [regions, setRegions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    document.title = 'Regions - Tanzania Basketball Federation';
    loadRegions();
  }, []);

  const loadRegions = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await regionAssociationService.getAll();
      setRegions(data || []);
    } catch (err) {
      setError(err.message || 'Failed to load regions');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredRegions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return regions;
    return regions.filter((region) =>
      [region?.associationName, region?.region, region?.chairpersonName]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query))
    );
  }, [regions, search]);

  return (
    <PublicBrowseShell
      title="Regions"
      subtitle="Regional basketball associations across Tanzania"
    >
      <div className="mb-6 max-w-md">
        <Input
          type="search"
          placeholder="Search regions..."
          iconName="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="bg-card rounded-lg border border-border p-12 card-shadow text-center">
          <Icon name="Loader2" size={40} className="text-primary mx-auto mb-4 animate-spin" />
          <p className="text-muted-foreground">Loading regions...</p>
        </div>
      ) : error ? (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} />
            <p className="font-medium">{error}</p>
          </div>
        </div>
      ) : filteredRegions?.length === 0 ? (
        <div className="bg-card rounded-lg border border-border p-12 card-shadow text-center">
          <Icon name="MapPin" size={40} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Regions Found</h3>
          <p className="text-muted-foreground">Try adjusting your search.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredRegions.map((region) => (
            <Link
              key={region.id}
              to={`/regions/${region.id}`}
              className="bg-card rounded-lg border border-border card-shadow p-6 hover:shadow-lg transition-all hover:scale-[1.02]"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-lg bg-white border border-border flex items-center justify-center overflow-hidden shrink-0">
                  {region?.logoUrl ? (
                    <Image
                      src={region.logoUrl}
                      alt={`${region.associationName || 'Region'} logo`}
                      className="w-full h-full object-contain p-1"
                    />
                  ) : (
                    <div className="w-full h-full bg-teal-600/10 flex items-center justify-center">
                      <Icon name="MapPin" size={26} className="text-teal-600" />
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold text-foreground truncate">{region?.associationName || 'Unnamed Association'}</h3>
                  <p className="text-sm text-muted-foreground truncate">{region?.region}</p>
                </div>
              </div>
              <div className="space-y-1 text-sm text-muted-foreground">
                {region?.chairpersonName && (
                  <p className="flex items-center gap-2 truncate">
                    <Icon name="User" size={14} className="shrink-0" /> {region.chairpersonName}
                  </p>
                )}
                <p className="flex items-center gap-2 capitalize">
                  <Icon name="BadgeCheck" size={14} className="shrink-0" /> {region?.registrationStatus || 'pending'}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </PublicBrowseShell>
  );
};

export default RegionsListPage;
